import React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, createStyles, makeStyles } from '@bb-ui/react-library';
import { CircularProgress, LinearProgress } from '@bb-ui/react-library/dist/components/Progress';
import { StatusApproval, StatusAway, StatusDisapproval } from '@bb-ui/icons/dist/medium';
import {
  ExecutionDetailResponseBody,
  ExecutionLogEntry,
  Status,
  TERMINAL_STATUSES,
} from '@lct-jobs/core';
import { JobCardList } from './basic/JobCardList';

// Step-event shape; defined locally until @lct-jobs/core is bumped to export ExecutionStepEventData.
type StepEventType = 'stepStarted' | 'stepProgress' | 'stepCompleted' | 'stepFailed';

interface StepProgress {
  processed: number;
  total: number;
}

type StepDetails = Record<string, string | number>;

interface StepEventData {
  eventType: StepEventType;
  step: string;
  progress?: StepProgress;
  // Arbitrary provider-supplied context, rendered generically.
  details?: StepDetails;
  // Only meaningful on stepFailed.
  errorMessage?: string;
}

const STEP_EVENT_TYPES: StepEventType[] = [
  'stepStarted',
  'stepProgress',
  'stepCompleted',
  'stepFailed',
];

const isCount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0;

function sanitizeProgress(value: unknown): StepProgress | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const { processed, total } = value as Record<string, unknown>;
  return isCount(processed) && isCount(total) ? { processed, total } : undefined;
}

// Keeps only string/finite-number entries, so a bad value can't throw while rendering.
function sanitizeDetails(value: unknown): StepDetails | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const entries = Object.entries(value).filter(
    (entry): entry is [string, string | number] =>
      typeof entry[1] === 'string' || (typeof entry[1] === 'number' && Number.isFinite(entry[1])),
  );
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

// A valid step/eventType makes it an event; malformed optional fields are dropped, not fatal.
function parseStepEvent(data: unknown): StepEventData | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const candidate = data as Record<string, unknown>;
  const { step, eventType } = candidate;
  if (typeof step !== 'string' || step.length === 0) return undefined;
  if (typeof eventType !== 'string' || !STEP_EVENT_TYPES.includes(eventType as StepEventType)) {
    return undefined;
  }
  return {
    eventType: eventType as StepEventType,
    step,
    progress: sanitizeProgress(candidate.progress),
    details: sanitizeDetails(candidate.details),
    errorMessage: typeof candidate.errorMessage === 'string' ? candidate.errorMessage : undefined,
  };
}

/** Whether any log entry is a step event, so parents only reserve space for the pipeline when it has content. */
export function hasStepEvents(logs?: ExecutionLogEntry[]): boolean {
  return (logs ?? []).some((entry) => parseStepEvent(entry.data) !== undefined);
}

type StepStatus = 'running' | 'done' | 'failed' | 'cancelled';

interface DerivedStep {
  name: string;
  status: StepStatus;
  startedAt?: string;
  endedAt?: string;
  progress?: StepProgress;
  details?: StepDetails;
  errorMessage?: string;
}

// Entries with an invalid timestamp sort last instead of making the comparator return NaN.
const timeOf = (entry: ExecutionLogEntry): number => {
  const time = new Date(entry.timestamp).getTime();
  return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time;
};

const compareByTime = (a: ExecutionLogEntry, b: ExecutionLogEntry): number => {
  const timeA = timeOf(a);
  const timeB = timeOf(b);
  if (timeA === timeB) return 0;
  return timeA < timeB ? -1 : 1;
};

// Steps are ordered by first appearance. `executionStatus` settles a step left 'running'
// when its final (best-effort SNS) event was dropped or the execution was cut short.
export function deriveSteps(
  logs?: ExecutionLogEntry[],
  executionStatus?: Status,
  executionEndDate?: string | Date,
): DerivedStep[] {
  const order: string[] = [];
  const byName = new Map<string, DerivedStep>();

  (logs ?? [])
    .filter((entry) => parseStepEvent(entry.data) !== undefined)
    .sort(compareByTime)
    .forEach((entry) => {
      const { eventType, step, progress, details, errorMessage } = parseStepEvent(
        entry.data,
      ) as StepEventData;

      let derived = byName.get(step);
      if (!derived) {
        derived = { name: step, status: 'running' };
        byName.set(step, derived);
        order.push(step);
      }
      const finished = derived.status === 'done' || derived.status === 'failed';

      switch (eventType) {
        case 'stepStarted':
          if (finished && derived.endedAt === entry.timestamp) {
            // Same attempt, delivered after its own terminal event.
            derived.startedAt = derived.startedAt ?? entry.timestamp;
            derived.details = details ? { ...derived.details, ...details } : derived.details;
          } else if (finished) {
            // A retry: forget the failed attempt so its duration and details don't carry over.
            derived.status = 'running';
            derived.startedAt = entry.timestamp;
            derived.endedAt = undefined;
            derived.progress = progress;
            derived.errorMessage = undefined;
            derived.details = details;
          } else {
            derived.status = 'running';
            derived.startedAt = derived.startedAt ?? entry.timestamp;
            derived.details = details ? { ...derived.details, ...details } : derived.details;
          }
          break;
        case 'stepProgress':
          if (progress) derived.progress = progress;
          derived.details = details ? { ...derived.details, ...details } : derived.details;
          break;
        case 'stepCompleted':
          derived.status = 'done';
          derived.endedAt = entry.timestamp;
          if (progress) derived.progress = progress;
          derived.details = details ? { ...derived.details, ...details } : derived.details;
          break;
        case 'stepFailed':
          derived.status = 'failed';
          derived.endedAt = entry.timestamp;
          derived.errorMessage = errorMessage;
          if (progress) derived.progress = progress;
          derived.details = details ? { ...derived.details, ...details } : derived.details;
          break;
        default:
          break;
      }
    });

  const steps = order.map((name) => byName.get(name) as DerivedStep);

  if (executionStatus && (TERMINAL_STATUSES as readonly Status[]).includes(executionStatus)) {
    let settled: StepStatus = 'failed';
    if (executionStatus === Status.COMPLETED) settled = 'done';
    else if (executionStatus === Status.CANCELED) settled = 'cancelled';
    const endTime = executionEndDate ? new Date(executionEndDate) : undefined;
    const endedAt = endTime && !Number.isNaN(endTime.getTime()) ? endTime.toISOString() : undefined;
    return steps.map((step) =>
      // Only 'running' steps are ambiguous; done/failed are left alone.
      step.status === 'running' ? { ...step, status: settled, endedAt } : step,
    );
  }

  return steps;
}

// Elapsed time while running; start-to-end once finished; unknown if no reliable endpoint.
function stepDurationMs(step: DerivedStep, now: number): number | undefined {
  if (!step.startedAt) return undefined;
  const start = new Date(step.startedAt).getTime();
  if (Number.isNaN(start)) return undefined;
  if (step.status === 'running') return now - start;
  if (!step.endedAt) return undefined;
  const end = new Date(step.endedAt).getTime();
  return Number.isNaN(end) ? undefined : end - start;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.max(ms, 0)}ms`;
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

// "invalidEmails" -> "Invalid Emails". Generic, no per-job vocabulary.
function humanizeKey(key: string): string {
  return key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
}

const ANCHOR_TAG_PATTERN = /^<a href="([^"]+)">(.+)<\/a>$/;

// Log details can carry untrusted text, so only absolute http(s) URLs become links.
function isSafeHref(href: string): boolean {
  try {
    const { protocol } = new URL(href);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
}

function renderDetailValue(value: string | number): React.ReactNode {
  if (typeof value === 'string') {
    const anchorMatch = value.match(ANCHOR_TAG_PATTERN);
    if (anchorMatch && isSafeHref(anchorMatch[1])) {
      const [, href, label] = anchorMatch;
      return (
        <a href={href} target="_blank" rel="noopener noreferrer">
          {label}
        </a>
      );
    }
    return value;
  }
  return value.toLocaleString();
}

// `retryCount: 0` is hidden: zero retries is the norm, not a result worth showing.
const DetailsLine: React.FC<{ details?: StepDetails; className: string }> = ({
  details,
  className,
}) => {
  if (!details) return null;
  const entries = Object.entries(details).filter(
    ([key, value]) => !(key === 'retryCount' && value === 0),
  );
  if (entries.length === 0) return null;

  return (
    <Typography component="div" className={className}>
      {entries.map(([key, value], index) => (
        <React.Fragment key={key}>
          {index > 0 && ' · '}
          {humanizeKey(key)}: {renderDetailValue(value)}
        </React.Fragment>
      ))}
    </Typography>
  );
};

// Lines the connector up with the centre of the status icons.
const CONNECTOR_OFFSET = 9;

const useStyles = makeStyles((theme) =>
  createStyles({
    list: {
      display: 'flex',
      flexDirection: 'column' as const,
    },
    connector: {
      width: 2,
      height: 12,
      marginLeft: CONNECTOR_OFFSET,
      backgroundColor: theme.palette.divider,
    },
    stepRow: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 8,
      padding: '6px 12px',
    },
    icon: {
      marginTop: 2,
      flexShrink: 0,
    },
    stepBody: {
      flex: 1,
      minWidth: 0,
    },
    stepName: {
      fontSize: 13,
      fontWeight: 600,
    },
    stepMeta: {
      fontSize: 12,
      color: theme.palette.text.secondary,
    },
    stepError: {
      fontSize: 12,
      color: theme.palette.error.main,
      marginTop: 2,
    },
    progressBar: {
      marginTop: 4,
    },
  }),
);

const StepStatusIcon: React.FC<{ status: StepStatus }> = ({ status }) => {
  const { t } = useTranslation();

  if (status === 'running') {
    return (
      <CircularProgress ariaLabel={t<string>('jobService.pipelineStatusRunning')} size="small" />
    );
  }
  const label = {
    done: t('jobService.pipelineStatusDone'),
    failed: t('jobService.pipelineStatusFailed'),
    cancelled: t('jobService.pipelineStatusCancelled'),
  }[status];
  return (
    <span role="img" aria-label={label}>
      {status === 'done' && <StatusApproval />}
      {status === 'failed' && <StatusDisapproval color="error" />}
      {status === 'cancelled' && <StatusAway />}
    </span>
  );
};

// Re-renders every second while a step is running so its elapsed time keeps moving between polls.
function useNow(active: boolean): number {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!active) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

interface PipelineErrorBoundaryProps {
  fallback: React.ReactNode;
}

/** Keeps a rendering problem in the pipeline from taking down the whole execution page. */
export class PipelineErrorBoundary extends React.Component<
  PipelineErrorBoundaryProps,
  { failed: boolean }
> {
  constructor(props: PipelineErrorBoundaryProps) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    const { children, fallback } = this.props;
    const { failed } = this.state;
    return failed ? fallback : children;
  }
}

export interface JobPipelineBoxProps {
  jobExecution?: ExecutionDetailResponseBody;
}

const PipelineCard: React.FC<JobPipelineBoxProps> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const logs = props.jobExecution?.logs;
  const executionStatus = props.jobExecution?.status;
  const executionEndDate = props.jobExecution?.endDate;
  const steps = React.useMemo(
    () => deriveSteps(logs, executionStatus, executionEndDate),
    [logs, executionStatus, executionEndDate],
  );
  const now = useNow(steps.some((step) => step.status === 'running'));

  // Jobs without step events render nothing.
  if (steps.length === 0) {
    return null;
  }

  const completedCount = steps.filter((step) => step.status === 'done').length;

  return (
    <JobCardList
      title={t('jobService.pipelineTitle')}
      subtitle={t('jobService.pipelineStepsComplete', {
        completed: completedCount,
        total: steps.length,
      })}
    >
      <div className={classes.list} data-testid="job-pipeline-box">
        {steps.map((step, index) => {
          const durationMs = stepDurationMs(step, now);
          const duration = durationMs === undefined ? undefined : formatDuration(durationMs);
          const progressPercent =
            step.progress && step.progress.total > 0
              ? Math.min(100, Math.round((step.progress.processed / step.progress.total) * 100))
              : undefined;

          return (
            <React.Fragment key={step.name}>
              {index > 0 && <div className={classes.connector} />}
              <div className={classes.stepRow} data-testid={`pipeline-step-${step.name}`}>
                <div className={classes.icon}>
                  <StepStatusIcon status={step.status} />
                </div>
                <div className={classes.stepBody}>
                  <Typography className={classes.stepName}>{step.name}</Typography>
                  {(duration || step.progress) && (
                    <Typography className={classes.stepMeta}>
                      {duration}
                      {step.progress
                        ? `${duration ? ' · ' : ''}${step.progress.processed.toLocaleString()} / ${step.progress.total.toLocaleString()}`
                        : ''}
                    </Typography>
                  )}
                  {step.status === 'running' && progressPercent !== undefined && (
                    <LinearProgress
                      className={classes.progressBar}
                      ariaLabel={t<string>('jobService.pipelineStepProgress', { step: step.name })}
                      variant="determinate"
                      value={progressPercent}
                    />
                  )}
                  {step.status === 'failed' && step.errorMessage && (
                    <Typography className={classes.stepError}>{step.errorMessage}</Typography>
                  )}
                  <DetailsLine details={step.details} className={classes.stepMeta} />
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </JobCardList>
  );
};

export const JobPipelineBox: React.FC<JobPipelineBoxProps> = (props) => {
  const { t } = useTranslation();
  const { jobExecution } = props;
  return (
    // Keyed on the log count so a bad entry doesn't leave the card stuck in its fallback.
    <PipelineErrorBoundary
      key={jobExecution?.logs?.length ?? 0}
      fallback={<Typography>{t('jobService.pipelineUnavailable')}</Typography>}
    >
      <PipelineCard jobExecution={jobExecution} />
    </PipelineErrorBoundary>
  );
};

export default JobPipelineBox;
